/**
 * One poll store per app (polls in the conversation, 2026-10-24): every surface that draws a poll
 * — the in-article `NCPoll` and the conversation panel's `ConversationPoll` — reads and writes the
 * same entry, keyed by WordPress poll id, so a vote in either moves both at once.
 *
 * Framework-free on purpose (the React side is `PollStoreContext.tsx`): the jest suite drives it in
 * node with fake transports. Where a vote goes:
 * - signed out → commentHub's anonymous route (`POST /api/polls/{site}/{pollId}/vote-anon`, keyed
 *   by this browser's random id in localStorage — `poll_anon_id` — so commentHub records the
 *   vote by segment and relays it; Have your say, 2026-10-27), else WordPress (`POST /api/poll`)
 *   when commentHub has no such route; `poll_voted_<id>` in localStorage is the record either way.
 *   A second tap (any option, the same one included) asks to sign in — signed out, a vote is never
 *   changed: a public −1 would let anyone script an option down (Guillaume, 2026-10-24).
 *   The ask is an inline card under the poll (`signInAsk` on the state, for `POLL_SIGN_IN_ASK_MS`),
 *   never the login dialog on its own (Guillaume, 2026-10-25);
 * - signed in → commentHub `PUT /api/polls/{site}/{pollId}/vote` (it relays to WordPress). Another
 *   choice changes the vote; the reader's own choice again REMOVES it (`DELETE`, the WordPress −1 —
 *   Guillaume, 2026-10-24), with an Undo for `POLL_UNDO_MS` (`undo` on the state);
 * - commentHub without the poll routes (404) → a first vote falls back to WordPress, a change or a
 *   removal does nothing (WordPress's public route only adds — it would double-count);
 * - on sign-in, a vote held only in localStorage is claimed once (`alreadyCounted: true`, with the
 *   browser's anonymous id so commentHub moves the anonymous row to the reader); a 409 adopts the
 *   server's choice (null = a removal still pending there).
 * Every successful vote also writes localStorage, so a reader who signs out never sees an unvoted
 * poll and votes into WordPress twice; a removal clears it.
 *
 * Every vote carries its context for commentHub's event log (`PollVoteMeta`): the surface it was
 * cast from, the article, and how the reader arrived (`referrerKind`).
 */
import type { PollVotedFrom } from 'shared-code/app-events'
import type {
  CommentHubPollRemoveResult,
  CommentHubPollVoteContext,
  CommentHubPollVoteRead,
  CommentHubPollVoteResult,
  CommentHubPollVotesRead,
} from 'shared-code/commenthub-api'
import type { DSPoll, DSPollChoiceCount } from 'shared-definitions/types'

export interface PollChoiceState {
  id: string
  label: string
  value: number
}

export interface PollState {
  id: string
  question: string
  choices: readonly PollChoiceState[]
  /** The reader's choice: commentHub's when signed in (once it answered), else localStorage's. */
  myChoiceId: string | null
  /** A vote is on its way — further taps wait. */
  voting: boolean
  /** The last vote this reader made on this page, and where (null until one): what the
   *  post-vote nudge ("Why this one?") keys on, on every surface (`shouldNudge`). Only a vote counted here sets it — never the page data,
   *  localStorage, the sign-in read or a list's `myChoiceId`, so a reader who voted on an
   *  earlier visit gets no nudge. `seq` grows with every vote. */
  lastVote: { seq: number; from: PollVotedFrom } | null
  /** The vote the reader just removed, while its Undo shows (`POLL_UNDO_MS`); null otherwise. */
  undo: { choiceId: string; seq: number } | null
  /** A signed-out reader tapped a choice again, while the "sign in to change it" card shows
   *  (`POLL_SIGN_IN_ASK_MS`); null otherwise. `seq` re-keys the card at each re-tap. */
  signInAsk: { seq: number } | null
  /** The reader posted a comment with this pick on this page (`markPosted`): the surfaces swap
   *  "Why this one?" for "Your take is in". Page-only, never read back; a removal resets it. */
  posted: boolean
}

/** How long "Vote removed · Undo" stays after a removal. */
export const POLL_UNDO_MS = 6000

/** How long the signed-out re-tap card stays. */
export const POLL_SIGN_IN_ASK_MS = 6000

/** WordPress, through the app's data source. */
export interface PollWpTransport {
  sendVote: (
    pollId: string,
    choiceId: string
  ) => Promise<{ ok: boolean; choices?: readonly DSPollChoiceCount[] }>
  fetchCounts: (pollId: string) => Promise<readonly DSPollChoiceCount[] | null>
}

/** commentHub's reader poll routes, bound to the brand's site. */
export interface PollHubTransport {
  vote: (
    token: string,
    pollId: string,
    choiceId: string,
    alreadyCounted?: boolean,
    context?: CommentHubPollVoteContext
  ) => Promise<CommentHubPollVoteResult>
  getVote: (token: string, pollId: string) => Promise<CommentHubPollVoteRead>
  remove: (
    token: string,
    pollId: string,
    context?: CommentHubPollVoteContext
  ) => Promise<CommentHubPollRemoveResult>
  /** A signed-out browser's vote (Have your say, 2026-10-27). Absent on an older transport: the
   *  vote goes to WordPress as before. */
  voteAnon?: (
    anonId: string,
    pollId: string,
    choiceId: string,
    context?: CommentHubPollVoteContext
  ) => Promise<CommentHubPollVoteResult>
  /** The reader's picks on several polls in one read (`registerAll`). Absent: one read per poll. */
  getVotes?: (token: string, pollIds: readonly string[]) => Promise<CommentHubPollVotesRead>
}

/** What a vote carries for commentHub's event log besides the surface (`PollVotedFrom`). */
export interface PollVoteMeta {
  /** The WordPress post the reader is on. */
  articlePostId?: number | null
  /** How the reader arrived — `referrerKind(document.referrer, location.hostname)`. */
  referrerKind?: string
}

export interface PollStorage {
  get: (key: string) => string | null
  set: (key: string, value: string) => void
  remove: (key: string) => void
}

export interface PollStoreDeps {
  wp: PollWpTransport
  /** Null when commentHub (or its site) is not configured: every vote goes to WordPress. */
  hub: PollHubTransport | null
  storage: PollStorage
}

/** What a tap did. `voted` is the one the analytics event fires for. */
export type PollVoteOutcome =
  | { kind: 'voted'; choiceId: string; changed: boolean; total: number }
  | { kind: 'removed'; choiceId: string; total: number }
  | { kind: 'unchanged' }
  | { kind: 'sign-in' }
  | { kind: 'failed' }

/**
 * Whether the post-vote nudge (bars fill, the composer's ring and focus, "Why this one?") should
 * play: a vote cast on this page (`lastVote`) in the article body, the conversation panel or the
 * rail card, not already played (`playedSeq`, the store's `nudged`). Never a vote from an earlier
 * visit, and never one from Have your say (`end`): its section is not the article's conversation.
 * (The mobile top bar stopped taking votes 2026-10-29 — its poll face opens the panel.)
 */
export function shouldNudge(lastVote: PollState['lastVote'], playedSeq: number): boolean {
  return (
    lastVote !== null &&
    (lastVote.from === 'panel' || lastVote.from === 'rail' || lastVote.from === 'article') &&
    lastVote.seq > playedSeq
  )
}

/** What a tap should do, from who the reader is and what they already picked. */
export type PollTapAction = 'wp-vote' | 'hub-vote' | 'hub-remove' | 'noop' | 'ask-sign-in'

export function pollTapAction(params: {
  signedIn: boolean
  hub: boolean
  myChoiceId: string | null
  choiceId: string
  voting: boolean
}): PollTapAction {
  const { signedIn, hub, myChoiceId, choiceId, voting } = params
  if (voting) {
    return 'noop'
  }
  if (signedIn && hub) {
    return myChoiceId === choiceId ? 'hub-remove' : 'hub-vote'
  }
  if (myChoiceId === null) {
    return 'wp-vote'
  }
  // Signed in without commentHub configured cannot change either — but asking to sign in a
  // reader who is signed in reads as a bug; nothing happens.
  return signedIn ? 'noop' : 'ask-sign-in'
}

export const pollStorageKey = (pollId: string): string => `poll_voted_${pollId}`

/** The browser's anonymous poll id: a random id this browser keeps, never the reader. */
export const POLL_ANON_ID_KEY = 'poll_anon_id'

/**
 * This browser's anonymous id, minted on first use (a uuid where the platform has one, else 32
 * random hex chars); null when storage refuses it — an id that cannot be kept would make every
 * vote a first vote, so the caller then votes through WordPress instead.
 */
export function pollAnonId(storage: PollStorage): string | null {
  const existing = storage.get(POLL_ANON_ID_KEY)
  if (existing && /^[A-Za-z0-9_-]{8,64}$/.test(existing)) {
    return existing
  }
  let id: string
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    id = crypto.randomUUID()
  } else {
    id = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('')
  }
  storage.set(POLL_ANON_ID_KEY, id)
  return storage.get(POLL_ANON_ID_KEY) === id ? id : null
}

const SEARCH_HOSTS =
  /(^|\.)(google|bing|duckduckgo|yahoo|ecosia|baidu|yandex|brave|qwant|startpage)\./i
const SOCIAL_HOSTS =
  /(^|\.)(facebook|fb|instagram|twitter|x|t|reddit|linkedin|threads|tiktok|youtube|pinterest|mastodon|bsky|discord|whatsapp|telegram|flipboard|news\.google)\.(com|co|me|net|social|app|org)$/i

/**
 * How the reader arrived, for commentHub's event log: `direct` (no referrer), `internal` (the
 * site itself, any subdomain), `search`, `social`, else `other`. Never the referrer itself.
 */
export function referrerKind(referrer: string, host: string): string {
  if (!referrer) {
    return 'direct'
  }
  let from: string
  try {
    from = new URL(referrer).hostname.toLowerCase()
  } catch {
    return 'other'
  }
  const apex = host.toLowerCase().replace(/^www\./, '')
  if (apex && (from === apex || from.endsWith(`.${apex}`))) {
    return 'internal'
  }
  if (SEARCH_HOSTS.test(from)) {
    return 'search'
  }
  if (SOCIAL_HOSTS.test(from)) {
    return 'social'
  }
  return 'other'
}

export function pollTotal(poll: Pick<PollState, 'choices'>): number {
  return poll.choices.reduce((sum, c) => sum + c.value, 0)
}

/** localStorage behind try/catch (private mode, blocked storage, the server). */
export const browserPollStorage: PollStorage = {
  get: key => {
    try {
      return typeof localStorage === 'undefined' ? null : localStorage.getItem(key)
    } catch {
      return null
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, value)
    } catch {
      // Storage refused: the vote still counted, only this browser forgets it.
    }
  },
  remove: key => {
    try {
      localStorage.removeItem(key)
    } catch {
      // Storage refused: nothing was stored either.
    }
  },
}

/** A poll's state from its page data — what the server HTML draws. */
export function pollStateFrom(poll: DSPoll): PollState {
  return {
    id: poll.id,
    question: poll.question,
    choices: poll.items.map(c => ({ id: c.id, label: c.answer, value: c.value })),
    myChoiceId: null,
    voting: false,
    lastVote: null,
    undo: null,
    signInAsk: null,
    posted: false,
  }
}

function withCounts(
  choices: readonly PollChoiceState[],
  counts: readonly DSPollChoiceCount[]
): PollChoiceState[] {
  const byId = new Map(counts.map(c => [c.choiceId, c.value]))
  return choices.map(c => (byId.has(c.id) ? { ...c, value: byId.get(c.id) ?? c.value } : c))
}

function moved(
  choices: readonly PollChoiceState[],
  from: string | null,
  to: string | null
): PollChoiceState[] {
  return choices.map(c => {
    if (c.id === to) {
      return { ...c, value: c.value + 1 }
    }
    if (c.id === from) {
      return { ...c, value: Math.max(0, c.value - 1) }
    }
    return c
  })
}

export class PollStore {
  private readonly deps: PollStoreDeps

  private readonly polls = new Map<string, PollState>()

  private readonly listeners = new Set<() => void>()

  private token: string | null = null

  // A claim is sent once per poll per signed-in token.
  private readonly synced = new Set<string>()

  // The sign-in read (and claim) still on its way, per poll: a vote waits for it.
  private readonly syncing = new Map<string, Promise<void>>()

  // A local count change made after a fresh-totals request left: re-applied on its answer, so a
  // request that went out before the vote cannot take the reader's own vote back off the bars.
  private readonly pendingDelta = new Map<
    string,
    { from: string | null; to: string | null; seq: number }[]
  >()

  private seq = 0

  // The last vote seq the post-vote nudge played for, per poll (`shouldNudge`): the panel
  // remounts on every open, so a vote nudges once, not at every reopen.
  private readonly nudged = new Map<string, number>()

  constructor(deps: PollStoreDeps) {
    this.deps = deps
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  get = (id: string): PollState | undefined => this.polls.get(id)

  /** commentHub's poll routes are configured: a signed-in reader can change and remove a vote. */
  get hubEnabled(): boolean {
    return this.deps.hub !== null
  }

  /** Seeds the entry from page data (a no-op when already known) and reads the reader's choice. */
  register(poll: DSPoll): void {
    if (!this.seed(poll)) {
      return
    }
    if (this.token) {
      this.startSync(poll.id, this.token)
    }
  }

  /**
   * Several polls at once (the end-of-article sets): seeded like `register`, the reader's picks
   * read in ONE commentHub call (`getVotes`) when signed in, each claim as `register` would make
   * it. Without `getVotes` on the transport, one read per poll as before.
   */
  registerAll(polls: readonly DSPoll[]): void {
    const fresh = polls.filter(poll => this.seed(poll))
    const token = this.token
    if (!token || fresh.length === 0) {
      return
    }
    const hub = this.deps.hub
    if (!hub?.getVotes) {
      for (const poll of fresh) {
        this.startSync(poll.id, token)
      }
      return
    }
    const ids = fresh.map(p => p.id).filter(id => !this.synced.has(id))
    if (ids.length === 0) {
      return
    }
    for (const id of ids) {
      this.synced.add(id)
    }
    const run = this.syncBatch(ids, token, hub.getVotes).finally(() => {
      for (const id of ids) {
        if (this.syncing.get(id) === run) {
          this.syncing.delete(id)
        }
      }
    })
    for (const id of ids) {
      this.syncing.set(id, run)
    }
  }

  /** The entry from page data; false when already known. */
  private seed(poll: DSPoll): boolean {
    if (this.polls.has(poll.id)) {
      return false
    }
    this.polls.set(poll.id, {
      ...pollStateFrom(poll),
      myChoiceId: this.deps.storage.get(pollStorageKey(poll.id)),
    })
    this.emit()
    return true
  }

  /** The session changed: signed in → commentHub's choice (and the claim); out → localStorage's. */
  setToken(token: string | null): void {
    if (token === this.token) {
      return
    }
    this.token = token
    this.synced.clear()
    for (const id of Array.from(this.polls.keys())) {
      this.patch(id, { signInAsk: null })
      if (token) {
        this.startSync(id, token)
      } else {
        this.patch(id, { myChoiceId: this.deps.storage.get(pollStorageKey(id)) })
      }
    }
  }

  /** A poll-aware comment list answered with the reader's choice (signed in only). */
  seedMyChoice(id: string, myChoiceId: string | null): void {
    const poll = this.polls.get(id)
    // Never while an Undo shows: a list fetched before the removal would put the pick back.
    if (
      this.token &&
      poll &&
      !poll.voting &&
      !poll.undo &&
      myChoiceId !== null &&
      poll.myChoiceId !== myChoiceId
    ) {
      this.patch(id, { myChoiceId })
    }
  }

  /** Fresh totals from WordPress (the page HTML is cached). Failure keeps the page's counts. */
  async refresh(id: string): Promise<void> {
    const started = this.seq
    let counts: readonly DSPollChoiceCount[] | null = null
    try {
      counts = await this.deps.wp.fetchCounts(id)
    } catch {
      return
    }
    const poll = this.polls.get(id)
    if (!counts || !poll) {
      return
    }
    let choices = withCounts(poll.choices, counts)
    for (const d of this.pendingDelta.get(id) ?? []) {
      if (d.seq > started) {
        choices = moved(choices, d.from, d.to)
      }
    }
    this.patch(id, { choices })
  }

  /**
   * The nudge for the poll's last vote, at most once: true (and marked played) when
   * `shouldNudge`, else false.
   */
  takeNudge(id: string): boolean {
    const lastVote = this.polls.get(id)?.lastVote ?? null
    if (!shouldNudge(lastVote, this.nudged.get(id) ?? 0) || !lastVote) {
      return false
    }
    this.nudged.set(id, lastVote.seq)
    return true
  }

  /** The reader posted a comment carrying this poll's pick (the composer's success): `posted`. */
  markPosted(id: string): void {
    this.patch(id, { posted: true })
  }

  /** A tap on a choice; `from` is the surface (the post-vote nudge's `lastVote`); `meta` the rest
   *  of the vote's context for commentHub's event log. */
  async vote(
    id: string,
    choiceId: string,
    from: PollVotedFrom = 'article',
    meta: PollVoteMeta = {}
  ): Promise<PollVoteOutcome> {
    // A tap right after sign-in waits for commentHub's answer (and the claim): a plain PUT sent
    // before the claim lands would count the reader's anonymous vote in WordPress twice.
    const syncing = this.syncing.get(id)
    if (syncing) {
      await syncing
    }
    const poll = this.polls.get(id)
    if (!poll || !poll.choices.some(c => c.id === choiceId)) {
      return { kind: 'unchanged' }
    }
    const token = this.token
    const action = pollTapAction({
      signedIn: token !== null,
      hub: this.deps.hub !== null,
      myChoiceId: poll.myChoiceId,
      choiceId,
      voting: poll.voting,
    })
    if (action === 'noop') {
      return { kind: 'unchanged' }
    }
    if (action === 'ask-sign-in') {
      this.askSignIn(id)
      return { kind: 'sign-in' }
    }
    const previous = poll.myChoiceId
    const context: CommentHubPollVoteContext = {
      surface: from,
      ...(meta.articlePostId ? { articlePostId: meta.articlePostId } : {}),
      ...(meta.referrerKind ? { referrerKind: meta.referrerKind } : {}),
    }
    this.patch(id, { voting: true })
    try {
      if (action === 'hub-remove' && token && this.deps.hub && previous !== null) {
        const result = await this.deps.hub.remove(token, id, context)
        // `unavailable`: commentHub has no remove route yet — nothing changes.
        return result.status === 'removed'
          ? this.applyRemove(id, previous, result.choices)
          : { kind: 'unchanged' }
      }
      if (action === 'hub-vote' && token && this.deps.hub) {
        const result = await this.deps.hub.vote(token, id, choiceId, undefined, context)
        if (result.status === 'recorded') {
          return this.applyVote(id, previous, result.choiceId, result.choices, from)
        }
        if (result.status === 'already_voted') {
          this.adoptServerChoice(id, result.choiceId)
          return { kind: 'unchanged' }
        }
        if (result.status === 'unknown_choice') {
          return { kind: 'failed' }
        }
        // `unavailable`: commentHub has no poll routes yet.
        if (previous !== null) {
          return { kind: 'unchanged' }
        }
      }
      // Signed out: commentHub's anonymous route, keyed by this browser's id, so the vote is
      // recorded by segment and relayed. Without the route (an older commentHub, or no pepper
      // there), or a browser whose storage keeps no id, WordPress as before.
      const anonVote = this.deps.hub?.voteAnon
      const anonId = anonVote ? pollAnonId(this.deps.storage) : null
      if (anonVote && anonId) {
        const result = await anonVote(anonId, id, choiceId, context)
        if (result.status === 'recorded') {
          return this.applyVote(id, previous, result.choiceId, result.choices, from)
        }
        if (result.status === 'already_voted') {
          // This browser voted before and forgot (cleared storage): the server's pick shows,
          // and the vote is not counted again.
          if (result.choiceId !== null) {
            this.deps.storage.set(pollStorageKey(id), result.choiceId)
          }
          this.adoptServerChoice(id, result.choiceId)
          return { kind: 'unchanged' }
        }
        if (result.status === 'unknown_choice') {
          return { kind: 'failed' }
        }
      }
      const sent = await this.deps.wp.sendVote(id, choiceId)
      if (!sent.ok) {
        return { kind: 'failed' }
      }
      return this.applyVote(id, previous, choiceId, sent.choices, from)
    } catch {
      return { kind: 'failed' }
    } finally {
      this.patch(id, { voting: false })
    }
  }

  private applyVote(
    id: string,
    previous: string | null,
    choiceId: string,
    counts: readonly DSPollChoiceCount[] | undefined,
    from: PollVotedFrom
  ): PollVoteOutcome {
    const poll = this.polls.get(id)
    if (!poll) {
      return { kind: 'unchanged' }
    }
    this.seq += 1
    const choices = counts
      ? withCounts(poll.choices, counts)
      : moved(poll.choices, previous, choiceId)
    if (!counts) {
      const deltas = this.pendingDelta.get(id) ?? []
      this.pendingDelta.set(id, [...deltas, { from: previous, to: choiceId, seq: this.seq }])
    }
    this.deps.storage.set(pollStorageKey(id), choiceId)
    this.patch(id, {
      choices,
      myChoiceId: choiceId,
      lastVote: { seq: this.seq, from },
      undo: null,
    })
    return {
      kind: 'voted',
      choiceId,
      changed: previous !== null && previous !== choiceId,
      total: pollTotal({ choices }),
    }
  }

  /** "Undo" after a removal: the removed choice, voted again (a +1 in WordPress). */
  async undoRemove(
    id: string,
    from: PollVotedFrom = 'article',
    meta: PollVoteMeta = {}
  ): Promise<PollVoteOutcome> {
    const undo = this.polls.get(id)?.undo
    if (!undo) {
      return { kind: 'unchanged' }
    }
    return this.vote(id, undo.choiceId, from, meta)
  }

  private applyRemove(
    id: string,
    previous: string,
    counts: readonly DSPollChoiceCount[] | undefined
  ): PollVoteOutcome {
    const poll = this.polls.get(id)
    if (!poll) {
      return { kind: 'unchanged' }
    }
    this.seq += 1
    const seq = this.seq
    const choices = counts ? withCounts(poll.choices, counts) : moved(poll.choices, previous, null)
    if (!counts) {
      const deltas = this.pendingDelta.get(id) ?? []
      this.pendingDelta.set(id, [...deltas, { from: previous, to: null, seq }])
    }
    this.deps.storage.remove(pollStorageKey(id))
    // lastVote goes too: the post-vote nudge ("Why this one?") is about a pick that is gone; and
    // `posted` with it — a new pick asks again.
    this.patch(id, {
      choices,
      myChoiceId: null,
      lastVote: null,
      posted: false,
      undo: { choiceId: previous, seq },
    })
    setTimeout(() => {
      if (this.polls.get(id)?.undo?.seq === seq) {
        this.patch(id, { undo: null })
      }
    }, POLL_UNDO_MS)
    return { kind: 'removed', choiceId: previous, total: pollTotal({ choices }) }
  }

  /** The signed-out re-tap card, for `POLL_SIGN_IN_ASK_MS` from the last re-tap. */
  private askSignIn(id: string): void {
    this.seq += 1
    const seq = this.seq
    this.patch(id, { signInAsk: { seq } })
    setTimeout(() => {
      if (this.polls.get(id)?.signInAsk?.seq === seq) {
        this.patch(id, { signInAsk: null })
      }
    }, POLL_SIGN_IN_ASK_MS)
  }

  /** The reader closed the card, or signed in from it. */
  dismissSignInAsk(id: string): void {
    this.patch(id, { signInAsk: null })
  }

  /** commentHub's stored choice wins; null = a removal still pending there (forget the local vote). */
  private adoptServerChoice(id: string, choiceId: string | null): void {
    if (choiceId === null) {
      this.deps.storage.remove(pollStorageKey(id))
    }
    this.patch(id, { myChoiceId: choiceId })
  }

  private startSync(id: string, token: string): void {
    const run = this.syncSignedIn(id, token).finally(() => {
      if (this.syncing.get(id) === run) {
        this.syncing.delete(id)
      }
    })
    this.syncing.set(id, run)
  }

  private async syncSignedIn(id: string, token: string): Promise<void> {
    const hub = this.deps.hub
    if (!hub || this.synced.has(id)) {
      return
    }
    this.synced.add(id)
    let read: CommentHubPollVoteRead
    try {
      read = await hub.getVote(token, id)
    } catch {
      return
    }
    await this.settleSignedIn(id, token, read)
  }

  /** The batch form of `syncSignedIn`: one read for every id, then each settles as it would alone. */
  private async syncBatch(
    ids: readonly string[],
    token: string,
    getVotes: NonNullable<PollHubTransport['getVotes']>
  ): Promise<void> {
    let read: CommentHubPollVotesRead
    try {
      read = await getVotes(token, ids)
    } catch {
      return
    }
    await Promise.all(
      ids.map(id =>
        this.settleSignedIn(
          id,
          token,
          read.status === 'unavailable'
            ? read
            : { status: 'ok', choiceId: read.votes.get(id) ?? null }
        )
      )
    )
  }

  /** commentHub's answer for one poll, signed in: its pick wins; none, and a local vote → the claim. */
  private async settleSignedIn(
    id: string,
    token: string,
    read: CommentHubPollVoteRead
  ): Promise<void> {
    const hub = this.deps.hub
    if (!hub || this.token !== token) {
      return
    }
    const local = this.deps.storage.get(pollStorageKey(id))
    if (read.status === 'unavailable') {
      this.patch(id, { myChoiceId: local })
      return
    }
    if (read.choiceId !== null) {
      this.patch(id, { myChoiceId: read.choiceId })
      return
    }
    if (local === null) {
      this.patch(id, { myChoiceId: null })
      return
    }
    // The sign-in claim: WordPress already counted this browser's vote. With the browser's
    // anonymous id (never minted here — a browser without one voted through WordPress),
    // commentHub moves the anonymous row to the reader.
    const anonId = this.deps.storage.get(POLL_ANON_ID_KEY)
    try {
      const claimed = anonId
        ? await hub.vote(token, id, local, true, { anonId })
        : await hub.vote(token, id, local, true)
      if (this.token !== token) {
        return
      }
      if (claimed.status === 'recorded') {
        // The server's row wins over what this browser remembered.
        if (claimed.choiceId !== local) {
          this.deps.storage.set(pollStorageKey(id), claimed.choiceId)
        }
        this.patch(id, { myChoiceId: claimed.choiceId })
      } else if (claimed.status === 'already_voted') {
        this.adoptServerChoice(id, claimed.choiceId)
      }
    } catch {
      // Not claimed: the local choice still shows; the next sign-in tries again.
    }
  }

  private patch(id: string, next: Partial<PollState>): void {
    const poll = this.polls.get(id)
    if (!poll) {
      return
    }
    const changed = Object.keys(next).some(k => poll[k] !== next[k])
    if (!changed) {
      return
    }
    this.polls.set(id, { ...poll, ...next })
    this.emit()
  }

  private emit(): void {
    for (const listener of Array.from(this.listeners)) {
      listener()
    }
  }
}
